import supabaseClient from '@/api/supabaseClient.ts';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import type { CommonIntakeCaptureSnapshot } from '@/pages/commonIntake/buildIntentCaptureSteps.ts';

export type SummaryPreview = {
  structured_summary: string;
  framework_routed: string;
  confidence_score: number;
  confidence_label: string;
  risk_classification: string;
  required_approval_path: string;
};

async function fetchSummaryPreview(snapshot: CommonIntakeCaptureSnapshot): Promise<SummaryPreview> {
  const { data, error } = await supabaseClient.functions.invoke('summary-preview', {
    body: {
      issue_title: snapshot.issue_title,
      issue_text: snapshot.short_description,
      core_constraint: snapshot.core_constraints?.filter(Boolean).join(',') || undefined,
      core_constraint_other: snapshot.core_constraint_detail || null,
      decision_required: snapshot.decision_needed,
      issue_visibility: snapshot.visibility ?? [],
      impact_if_unresolved: snapshot.impact ?? [],
      time_sensitivity: snapshot.time_sensitivity,
    },
  });

  if (error && error instanceof FunctionsHttpError) {
    const err = await error.context.json();
    throw new Error(err.errors?.join(', ') ?? 'Summary preview failed');
  }

  return data.data as SummaryPreview;
}

type UseSummaryPreviewOptions = {
  /** When false, skips the AI call (e.g. org has no governed decision credits). */
  enabled?: boolean;
};

const useSummaryPreview = (
  snapshot: CommonIntakeCaptureSnapshot,
  options: UseSummaryPreviewOptions = {},
) => {
  const hasMinimumData = !!(snapshot.issue_title && snapshot.short_description);
  const creditsAllowed = options.enabled !== false;

  return useQuery({
    queryKey: ['summary-preview', snapshot],
    queryFn: () => fetchSummaryPreview(snapshot),
    enabled: hasMinimumData && creditsAllowed,
    staleTime: Infinity,
    retry: 1,
  });
};

export default useSummaryPreview;
